import type {Mapper} from '@automapper/core';
import {constructUsing, createMap, forMember, mapFrom} from '@automapper/core';
import type {IMapping} from '@igp/shared/auto-mapper';
import type {KeycloakLog} from '@igp/shared/contract/pam';

import {
    KeycloakLogDataGridColumnTypeName,
    KeycloakLogQueryFieldsTypeName,
    KeycloakLogTypeName,
    KeycloakLogViewModelKeysArrayTypeName,
    KeycloakLogViewModelKeysTypeName,
    KeycloakLogViewModelTypeName,
} from './constants';
import type {KeycloakLogDataGridColumn, KeycloakLogQueryFields, KeycloakLogViewModel, KeycloakLogViewModelKeys} from './types';

export class KeycloakLogMapping implements IMapping {
    createMapping(mapper: Mapper): void {
        createMap<KeycloakLog, KeycloakLogViewModel>(
            mapper,
            KeycloakLogTypeName,
            KeycloakLogViewModelTypeName,
            forMember(
                vm => vm.logId,
                mapFrom(m => m.log_id)
            ),
            forMember(
                vm => vm.dateSeconds,
                mapFrom(m => m.date?.seconds)
            ),
            forMember(
                vm => vm.operationType,
                mapFrom(m => m.operation_type)
            ),
            forMember(
                vm => vm.resourceType,
                mapFrom(m => m.resource_type)
            ),
            forMember(
                vm => vm.operatorUuid,
                mapFrom(m => m.operator_uuid)
            ),
            forMember(
                vm => vm.targetUserUuid,
                mapFrom(m => m.target_user_uuid)
            ),
            forMember(
                vm => vm.details,
                mapFrom(m => (typeof m.details === 'string' ? m.details : JSON.stringify(m.details)))
            )
        );

        createMap<KeycloakLogViewModelKeys, KeycloakLogQueryFields>(
            mapper,
            KeycloakLogViewModelKeysTypeName,
            KeycloakLogQueryFieldsTypeName,
            constructUsing((m: KeycloakLogViewModelKeys) => {
                const mapping: Record<KeycloakLogViewModelKeys, KeycloakLogQueryFields> = {
                    logId: 'log_id',
                    dateSeconds: 'date.seconds',
                    operationType: 'operation_type',
                    resourceType: 'resource_type',
                    operatorUuid: 'operator_uuid',
                    targetUserUuid: 'target_user_uuid',
                    details: 'details',
                };
                return mapping[m];
            })
        );

        createMap<KeycloakLogDataGridColumn, KeycloakLogViewModelKeys[]>(
            mapper,
            KeycloakLogDataGridColumnTypeName,
            KeycloakLogViewModelKeysArrayTypeName,
            constructUsing((m: KeycloakLogDataGridColumn) => {
                const mapping: Record<KeycloakLogDataGridColumn, KeycloakLogViewModelKeys[]> = {
                    date: ['dateSeconds'],
                    operator: ['operatorUuid'],
                    action: ['resourceType', 'operationType'],
                    targetUser: ['targetUserUuid'],
                    details: ['details'],
                };

                return mapping[m];
            })
        );
    }
}
